import {
  claimsVolumeTrendResponseSchema,
  dataConfidenceResponseSchema,
  dashboardResponseSchema,
  identityContextSchema,
  metricValueSchema,
  safetyReviewResponseSchema,
  tenantAccessSchema,
  tenantContextSchema,
  type ClaimsVolumeTrendResponse,
  type ClaimsVolumeSourceChoice,
  type DataConfidenceResponse,
  type DashboardResponse,
  type IdentityContext,
  type MetricValue,
  type SafetyReviewResponse,
  type TenantAccess,
  type TenantContext,
  top5ResponseSchema,
  type Top5Response,
} from "@broadview/contracts";
import { clientError } from "./errors";
import type {
  BroadviewApiClient,
  BroadviewClientOptions,
  DashboardRequest,
  MetricRequest,
  TenantScopedRequest,
} from "./client";
import { createLiveTransport } from "./transport";

function parseWithSchema<T>(schema: { parse(value: unknown): T }, payload: unknown): T {
  return schema.parse(payload);
}

function requireMatchingTenant<T extends Readonly<{ tenantId: string }>>(
  payload: T,
  tenantId: string,
): T {
  if (payload.tenantId !== tenantId) {
    throw clientError(
      502,
      "CONTRACT_MISMATCH",
      "Broadview API returned data for a different tenant context.",
    );
  }
  return payload;
}

/**
 * Provides only the read routes whose handoff response contracts expose enough
 * context to be validated in the browser. Unfrozen record envelopes fail
 * closed rather than accepting unvalidated tenant data.
 */
export function createLiveClient(options: BroadviewClientOptions): BroadviewApiClient {
  if (!options.apiOrigin || !options.getAccessToken) {
    throw clientError(
      503,
      "LIVE_ADAPTER_DISABLED",
      "Live API mode requires the approved API origin and an application-provided token reader.",
    );
  }

  const getJson = createLiveTransport({
    apiOrigin: options.apiOrigin,
    getAccessToken: options.getAccessToken,
    fetchImpl: options.fetchImpl,
  });

  return {
    mode: "live",
    identity: {
      me: () =>
        getJson<IdentityContext>({ path: "/me" }, (payload) =>
          parseWithSchema<IdentityContext>(identityContextSchema, payload),
        ),
      listTenants: () =>
        getJson<readonly TenantAccess[]>({ path: "/tenants" }, (payload) => {
          const envelope =
            payload && typeof payload === "object"
              ? (payload as { items?: unknown }).items
              : undefined;
          return parseWithSchema<readonly TenantAccess[]>(
            { parse: (value) => tenantAccessSchema.array().parse(value) },
            envelope,
          );
        }),
      getTenantContext: async (tenantId) => {
        const context = await getJson<TenantContext>(
          { path: `/tenants/${encodeURIComponent(tenantId)}/context` },
          (payload) => parseWithSchema<TenantContext>(tenantContextSchema, payload),
        );
        return requireMatchingTenant(context, tenantId);
      },
    },
    dashboard: {
      get: async ({ tenantId, view }: DashboardRequest) => {
        const dashboard = await getJson<DashboardResponse>(
          { path: `/dashboard/${encodeURIComponent(view)}`, tenantId },
          (payload) => parseWithSchema<DashboardResponse>(dashboardResponseSchema, payload),
        );
        requireMatchingTenant(dashboard, tenantId);
        if (view === "financial-intelligence" || view === "effectiveness-prevention") {
          if (dashboard.view !== view) {
            throw clientError(
              502,
              "CONTRACT_MISMATCH",
              "The API returned a different intelligence view.",
            );
          }
          const widgets = dashboard.widgets.map((widget) => {
            const metric = metricValueSchema.safeParse(widget.payload);
            if (!metric.success) {
              throw clientError(
                502,
                "CONTRACT_MISMATCH",
                "An intelligence widget has invalid metric evidence.",
              );
            }
            return { ...widget, payload: requireMatchingTenant(metric.data, tenantId) };
          });
          return dashboardResponseSchema.parse({ ...dashboard, widgets });
        }
        return dashboard;
      },
      getMetric: async ({ tenantId, definitionId }: MetricRequest): Promise<MetricValue> => {
        const metric = await getJson<MetricValue>(
          { path: `/metrics/${encodeURIComponent(definitionId)}`, tenantId },
          (payload) => parseWithSchema<MetricValue>(metricValueSchema, payload),
        );
        return requireMatchingTenant(metric, tenantId);
      },
    },
    claimsVolumeTrend: {
      get: async ({
        tenantId,
        sourceChoice = "SOURCE_RANKED",
      }: TenantScopedRequest & {
        sourceChoice?: ClaimsVolumeSourceChoice;
      }): Promise<ClaimsVolumeTrendResponse> => {
        const response = await getJson<ClaimsVolumeTrendResponse>(
          {
            path:
              sourceChoice === "SOURCE_RANKED"
                ? "/claims-volume-trend"
                : `/claims-volume-trend?sourceChoice=${encodeURIComponent(sourceChoice)}`,
            tenantId,
          },
          (payload) =>
            parseWithSchema<ClaimsVolumeTrendResponse>(claimsVolumeTrendResponseSchema, payload),
        );
        const available = response.series.filter((series) => series.status === "AVAILABLE");
        const demoRunIds = new Set(available.map((series) => series.calculationRunId));
        const demoCutoffs = new Set(available.map((series) => series.dataCutoffAt));
        if (
          sourceChoice === "GOVERNED_DEMONSTRATION" &&
          (response.sourceChoice !== sourceChoice ||
            (available.length > 0 && (demoRunIds.size !== 1 || demoCutoffs.size !== 1)) ||
            (available.some((series) => series.metricId === "K005") &&
              !available.some((series) => series.metricId === "K004")) ||
            response.series.some(
              (series) =>
                series.status === "AVAILABLE" &&
                (series.dataState !== "SYNTHETIC" ||
                  series.authorityClass !== "DEMONSTRATION_ONLY" ||
                  !series.productionUseProhibited ||
                  series.provenance.length === 0 ||
                  series.provenance.some((item) => item.authorityClass !== "DEMONSTRATION_ONLY")),
            ))
        ) {
          throw clientError(
            502,
            "CONTRACT_MISMATCH",
            "The API did not honor the explicit governed demonstration source choice. No data was substituted.",
          );
        }
        return requireMatchingTenant(response, tenantId);
      },
    },
    dataConfidence: {
      get: async ({ tenantId }: TenantScopedRequest): Promise<DataConfidenceResponse> => {
        const response = await getJson<DataConfidenceResponse>(
          { path: "/data-confidence", tenantId },
          (payload) =>
            parseWithSchema<DataConfidenceResponse>(dataConfidenceResponseSchema, payload),
        );
        return requireMatchingTenant(response, tenantId);
      },
    },
    top5: {
      get: async ({ tenantId }: TenantScopedRequest): Promise<Top5Response> => {
        const response = await getJson<Top5Response>({ path: "/top5", tenantId }, (payload) =>
          parseWithSchema<Top5Response>(top5ResponseSchema, payload),
        );
        return requireMatchingTenant(response, tenantId);
      },
    },
    safetyReview: {
      get: async ({ tenantId }: TenantScopedRequest): Promise<SafetyReviewResponse> => {
        const response = await getJson<SafetyReviewResponse>(
          { path: "/safety-review", tenantId },
          (payload) => parseWithSchema<SafetyReviewResponse>(safetyReviewResponseSchema, payload),
        );
        return requireMatchingTenant(response, tenantId);
      },
    },
    problemRecords: {
      list: async (_request: TenantScopedRequest) => {
        throw clientError(
          502,
          "CONTRACT_MISMATCH",
          "Live Problem Record reads remain disabled until the coordinator freezes their typed response envelope.",
        );
      },
      get: async (_request) => {
        throw clientError(
          502,
          "CONTRACT_MISMATCH",
          "Live Problem Record reads remain disabled until the coordinator freezes their typed response envelope.",
        );
      },
    },
  };
}
